import { useEffect } from "react";
import moment from "moment";

const getAnnouncementDate = (announcement) =>
  announcement.created_on || announcement.created_at || announcement.created_date;

const isNewAnnouncement = (announcement) => {
  const createdDate = getAnnouncementDate(announcement);

  if (!createdDate) return false;

  return moment(createdDate).isAfter(moment().subtract(30, "days"));
};

const sortByNewest = (announcements) =>
  [...announcements].sort((prev, next) => {
    const prevDate = getAnnouncementDate(prev);
    const nextDate = getAnnouncementDate(next);

    if (!prevDate && !nextDate) return 0;
    if (!prevDate) return 1;
    if (!nextDate) return -1;

    return moment(nextDate).valueOf() - moment(prevDate).valueOf();
  });

const ANNOUNCEMENTS = sortByNewest([
  {
    id: 5,
    title: "Operations",
    description:
      "Your complete guest operations platform for tasks, inspections, scheduling, inventory, and team coordination.",
    link: "https://intercom.help/ruebarueapp/en/articles/15611420-operations",
    created_on: "2026-06-25 19:09:20.981029",
  },
  {
    id: 10,
    title: "Introducing AI Guest Reply",
    description:
      "AI Guest Reply analyzes guest intent, identifies the correct reservation, pulls relevant guidebook content and saved responses, and generates a booking-specific suggested reply — all in seconds.",
    link: "https://intercom.help/ruebarueapp/en/articles/14167973-ai-guest-reply",
    created_on: "2026-03-23 10:04:16.390894",
  },
  {
    id: 12,
    title: "Inspection Board",
    description:
      "Inspection Board helps your team quickly track property readiness, update statuses, add notes, and stay on top of arrivals all in one place.",
    link: "https://intercom.help/ruebarueapp/en/articles/14982769-inspection-board",
    created_on: "2026-05-06 17:27:30.812706",
  },
  {
    id: 13,
    title: "Activate Smart Locks & Automated Door Codes",
    description:
      "Connect and manage smart locks across your properties, automatically generate time-bound door codes for every reservation, and deliver secure guest access through automated messages and digital guidebooks.",
    link: "https://intercom.help/ruebarueapp/en/articles/15055147-smart-locks",
    created_on: "2026-05-11 16:49:02.204561",
  },
  {
    id: 14,
    title: "Signatures",
    description:
      "You can now add your name and signature on the Profile page (https://app.ruebarue.com/profile). Once configured, your signature will automatically be included when you reply to guest messages, helping provide a more personal and professional communication experience.",
    link: "https://intercom.help/ruebarueapp/en/articles/15363333-signatures",
    created_on: "2026-06-03 17:15:23.245956",
  },
  {
    id: 15,
    title: "Inventory Management",
    description:
      "Use Inventory to track storage locations, supply items, stock levels, stock movements, and inventory value across your operations.",
    link: "https://intercom.help/ruebarueapp/en/articles/15505476-inventory-management",
    created_on: "2026-06-16 20:02:22.130132",
  },
  {
    id: 16,
    title: "Airbnb Integration",
    description:
      "Connect one or more Airbnb accounts to view and reply to Airbnb messages without leaving RueBaRue. Airbnb conversations are clearly identified with the Airbnb icon, and replies are automatically sent through Airbnb.",
    link: "https://intercom.help/ruebarueapp/en/articles/15879519-airbnb-messaging-integration",
    created_on: "2026-07-08 18:56:46.235245",
  },
  {
    id: 17,
    title: "Internal Notes",
    description:
      "Add private notes to guest conversations to document phone calls, maintenance updates, reminders, and other important context. Internal Notes help your team stay aligned and are never sent to guests.",
    link: "https://intercom.help/ruebarueapp/en/articles/15979519-internal-notes" ,
    created_on: "2026-07-16 18:56:46.235245",
  },
]);

const WhatsNew = () => {
  useEffect(() => {
    document.title = "What's New - RueBaRue";
  }, []);

  const announcements = ANNOUNCEMENTS;

  return (
    <section id="whats-new-page" className="rbr-whats-new-page">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">What's New</h1>
      </div>

      <div id="hms-main-body">
        {announcements.length === 0 ? (
          <div className="uk-card uk-card-default uk-card-body rbr-whats-new-empty">
            No announcements found.
          </div>
        ) : (
          <div className="rbr-whats-new-list">
            {announcements.map((announcement) => {
              const createdDate = getAnnouncementDate(announcement);

              return (
                <article
                  className="uk-card uk-card-default uk-card-body rbr-whats-new-card"
                  key={`announcement_${announcement.id}`}
                >
                  {announcement.image_url && (
                    <img
                      className="rbr-whats-new-image"
                      src={announcement.image_url}
                      alt=""
                    />
                  )}

                  {announcement.feature_category && (
                    <div className="rbr-whats-new-category">
                      {announcement.feature_category}
                    </div>
                  )}

                  <div className="rbr-whats-new-card-header">
                    <h2>{announcement.title}</h2>
                    {isNewAnnouncement(announcement) && (
                      <span className="rbr-whats-new-badge">New</span>
                    )}
                  </div>

                  {createdDate && (
                    <div className="rbr-whats-new-date">
                      {moment(createdDate).format("MMMM D, YYYY")}
                    </div>
                  )}

                  <p className="rbr-whats-new-description">
                    {announcement.description}
                  </p>

                  <div className="rbr-whats-new-actions">
                    {announcement.video_url && (
                      <a
                        className="uk-button hms-btn hms-white-btn rbr-whats-new-video"
                        href={announcement.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch Video
                      </a>
                    )}

                    {announcement.link && (
                      <a
                        className="uk-button hms-btn hms-red-btn rbr-whats-new-link"
                        href={announcement.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn More
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhatsNew;
